import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompletionNotificationDto,
  CreateCompletionNotificationDto,
  UpdateCompletionNotificationDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import axios from 'axios';
import { completionEventsQueryKeys } from '@/features/intake/queryKeys/completionEvents';
import { completionNotificationsQueryKeys } from '@/features/intake/queryKeys/completionNotifications';

export const useCompletionNotificationsApi = (httpClient: AxiosInstance) => {
  const baseUrl = '/completion-notifications';
  const queryClient = useQueryClient();

  const useGetByEvent = (completionEventId: number) =>
    useQuery({
      queryKey: completionNotificationsQueryKeys.byEvent(completionEventId),
      queryFn: async (): Promise<CompletionNotificationDto | null> => {
        try {
          const { data } = await httpClient.get(`${baseUrl}/by-event/${completionEventId}`);
          return data;
        } catch (error) {
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            return null;
          }
          throw error;
        }
      },
      enabled: completionEventId > 0,
    });

  const useCreate = () =>
    useMutation({
      mutationFn: async (
        dto: CreateCompletionNotificationDto,
      ): Promise<CompletionNotificationDto> => {
        const { data } = await httpClient.post(baseUrl, dto);
        return data;
      },
      onSuccess: (data) => {
        queryClient.setQueryData(
          completionNotificationsQueryKeys.byEvent(data.completionEventId),
          data,
        );
      },
    });

  const useUpdate = () =>
    useMutation({
      mutationFn: async ({
        id,
        dto,
      }: {
        id: number;
        dto: UpdateCompletionNotificationDto;
      }): Promise<CompletionNotificationDto> => {
        const { data } = await httpClient.patch(`${baseUrl}/${id}`, dto);
        return data;
      },
      onSuccess: (data) => {
        queryClient.setQueryData(
          completionNotificationsQueryKeys.byEvent(data.completionEventId),
          data,
        );
      },
    });

  const useDelete = () =>
    useMutation({
      mutationFn: async ({ id }: { id: number; completionEventId: number }): Promise<void> => {
        await httpClient.delete(`${baseUrl}/${id}`);
      },
      onSuccess: (_, { completionEventId }) => {
        queryClient.setQueryData(completionNotificationsQueryKeys.byEvent(completionEventId), null);
      },
    });

  const useSend = () =>
    useMutation({
      mutationFn: async ({
        id,
        closeIdea,
        includeOwner,
      }: {
        id: number;
        closeIdea?: boolean;
        includeOwner?: boolean;
      }): Promise<CompletionNotificationDto> => {
        const { data } = await httpClient.post(`${baseUrl}/${id}/send`, {
          closeIdea,
          includeOwner,
        });
        return data;
      },
      onSuccess: (data) => {
        queryClient.setQueryData(
          completionNotificationsQueryKeys.byEvent(data.completionEventId),
          data,
        );
        queryClient.invalidateQueries({ queryKey: completionEventsQueryKeys.list() });
      },
    });

  return { useGetByEvent, useCreate, useUpdate, useDelete, useSend };
};
